import React, { useRef, useEffect, useState, useMemo } from "react";
import { ResultCardProductProps } from "@usereactify/search";
import Flickity from "react-flickity-component";
import { useAppContext } from "../../../context/AppContext";

import Swatches from "./swatches";
import Icons from "../Icons/icons.js";

import { formatMoney } from "../../../utils/formatMoney.ts";
import { generateShopifyImageUrl } from "../../../utils/generateShopifyImageUrl.ts";
import { checkCurrentPageURL } from "../../../utils/checkURL";
declare global {
  interface Window {
    _swat?: any;
    SwymCallbacks?: Array<(swat: any) => void>;
  }
}

export type ProductCardProps = React.ComponentProps<NonNullable<ResultCardProductProps["render"]>> & {
  isSlider?: boolean;
};

export const ProductCard: React.FC<ProductCardProps> = ({ isSlider = true, ...props }) => {
  const { activeView } = useAppContext();
  const { isMen, isWomen } = checkCurrentPageURL();

  const onSale = props?.onSale;
  const comingSoon = props?.product?.tags?.includes("coming-soon");

  const currentPageUrl = window.location.href.split("?")[0];
  const isSearchPage = currentPageUrl.includes("/search");
  const isProductPage = currentPageUrl.includes("/products");

  const customBadge = props?.product?.tags?.find((tag: string) => tag.includes("badge:"));
  const customBadgeValue = customBadge?.split(":")[1];

  const flickityRef = useRef(null);

  const prices = useMemo(() => {
    const selectedCurrency = window.Shopify?.currency?.active || "AUD";
    const firstVariant = props?.product?.variants?.[0];
    const prices = firstVariant?.presentment_prices?.find((prices: any) => prices?.price?.currency_code === selectedCurrency);
    return prices;
  }, [props?.product?.variants]);

  useEffect(() => {
    // resolve link and carousel arrow click event propagation issue
    setTimeout(() => {
      const flickityInstance = flickityRef.current;
      if (flickityInstance) {
        // Query the Flickity arrows
        const prevArrow = flickityInstance.flkty.prevButton.element;
        const nextArrow = flickityInstance.flkty.nextButton.element;

        // Add click event listeners to prevent event propagation
        const handleNext = (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          flickityInstance.flkty.next();
        };

        const handlePrev = (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          flickityInstance.flkty.previous();
        };

        prevArrow?.addEventListener("click", handlePrev);
        nextArrow?.addEventListener("click", handleNext);

        // Clean up event listeners on component unmount
        return () => {
          prevArrow?.removeEventListener("click", handlePrev);
          nextArrow?.removeEventListener("click", handleNext);
        };
      }
    }, 100);
    
    // Re-initialize Swym for new products
    if (window._swat && window.SwymCallbacks) {
      try {
        window.SwymCallbacks.forEach((callback) => callback(window._swat));
      } catch (error) {
        console.error("Error running Swym callback:", error);
      }
    }
  }, []);

  const flickityOptions = React.useMemo(() => {
    return {
      cellAlign: "left",
      pageDots: true,
      contain: true,
      fade: false,
      autoPlay: false,
      prevNextButtons: true,
      draggable: true,
      cellSelector: ".product-card-slides",
      initialIndex: 1,
      percentPosition: false,
      arrowShape: {
        x0: 35,
        x1: 65,
        y1: 30,
        x2: 70,
        y2: 25,
        x3: 45,
      },
    };
  }, []);

  if (window.innerWidth <= 768) {
    flickityOptions.initialIndex = 0;
  }

  let hoverImage = props?.product?.images[0];
  let sliderImages = props?.product?.images.slice(0, 3);

  if (isMen) {
    hoverImage = props?.product?.images.find((image) => image.src.includes("_men")) || props?.product?.images[0];
    sliderImages = props?.product?.images.filter((image) => image.src.includes("_men")).slice(0, 3);
  }
  if (isWomen) {
    hoverImage = props?.product?.images.find((image) => !image.src.includes("_men")) || props?.product?.images[0];
    sliderImages = props?.product?.images.filter((image) => !image.src.includes("_men")).slice(0, 3);
  }

  // Ensure the sliderImages array has at least 3 images
  if (sliderImages.length < 3) {
    const additionalImages = props?.product?.images.slice(sliderImages.length, 3);
    sliderImages = sliderImages.concat(additionalImages);
  }
  // Final adjustment to ensure there are 3 images in sliderImages
  sliderImages = sliderImages.slice(0, 3);

  const productSizes = props?.product?.variants?.map((variant) => variant);

  return (
    <div ref={props.itemRef} className={`rs__result-card-product product-card is-slider-${isSlider}`}>
      <figure className="product-featured-image thb-hover">
        {comingSoon && <span className="badge coming-soon top left">coming soon</span>}

        {customBadgeValue && !comingSoon && <span className="badge top left">{customBadgeValue}</span>}

        {isSlider && <Wishlist productId={props?.product?.id} />}
        <a
          className="rs__result-card-product__link product-featured-image-link"
          onClick={props.handleClick}
          href={`/products/${props.product.handle}`}
        >
          {isSlider && (
            <div className="product-card-slider main-product-card-slider">
              <Flickity
                className={"carousel"}
                elementType={"div"}
                options={flickityOptions}
                disableImagesLoaded={false} // default false
                reloadOnUpdate // default false
                static // default false
                ref={flickityRef}
              >
                {sliderImages.map((image, index) => (
                  <div key={index} className="product-card-slides">
                    <img
                      className="rs__result-card-product__image"
                      src={generateShopifyImageUrl(image.src, "1080")}
                      srcSet={`
                        ${generateShopifyImageUrl(image.src, "320")} 320w,
                        ${generateShopifyImageUrl(image.src, "480")} 480w,
                        ${generateShopifyImageUrl(image.src, "720")} 720w,
                        ${generateShopifyImageUrl(image.src, "1080")} 1080w
                      `}
                      sizes="(max-width: 319px) 320px, (max-width: 479px) 480px, (max-width: 719px) 720px, 1080px"
                      loading="lazy"
                    />
                  </div>
                ))}
              </Flickity>
            </div>
          )}
          <div className="product-hover-image">
            {hoverImage?.src && (
              <img
                className="rs__result-card-product__image product-primary-image"
                src={generateShopifyImageUrl(hoverImage?.src, "1080")}
                srcSet={`
                ${generateShopifyImageUrl(hoverImage?.src, "320")} 320w,
                ${generateShopifyImageUrl(hoverImage?.src, "480")} 480w,
                ${generateShopifyImageUrl(hoverImage?.src, "720")} 720w,
                ${generateShopifyImageUrl(hoverImage?.src, "1080")} 1080w
              `}
                sizes="(max-width: 319px) 320px, (max-width: 479px) 480px, (max-width: 719px) 720px, 1080px"
                loading="lazy"
              />
            )}
          </div>
          {activeView === "view-product" && (
            <div className="product-hover-image product-ghost-image-wrapper">
              {props?.product?.images.map((image, index) => {
                if (image.src.includes("_ghost")) {
                  return (
                    <img
                      key={index}
                      className="product-primary-image product-ghost-image"
                      src={generateShopifyImageUrl(image.src, "1080")}
                      srcSet={`
                      ${generateShopifyImageUrl(image.src, "320")} 320w,
                      ${generateShopifyImageUrl(image.src, "480")} 480w,
                      ${generateShopifyImageUrl(image.src, "720")} 720w,
                      ${generateShopifyImageUrl(image.src, "1080")} 1080w
                    `}
                      sizes="(max-width: 319px) 320px, (max-width: 479px) 480px, (max-width: 719px) 720px, 1080px"
                      loading="lazy"
                    />
                  );
                }
              })}
            </div>
          )}
        </a>
      </figure>
      <div className="product-card-info">
        <div>
          <span className="rs__result-card-product__title product-card-title text-md text-accent">
            {props.product.title}
          </span>
          <div className="price">
            {props.onSale && (
              <del
                className="rs__result-card-product__price-sale"
                style={{
                  textDecoration: "line-through",
                }}
              >
                <span className="amount">{formatMoney(prices?.compare_at_price?.amount * 100)} </span>
              </del>
            )}
            <span className="rs__result-card-product__price">{formatMoney(prices?.price?.amount * 100)}</span>
          </div>
          <Swatches metafields={props?.product?.metafields} productHandle={props.product.handle}></Swatches>
          {productSizes && <ProductSizes productHandle={`/products/${props.product.handle}`} sizes={productSizes} />}
        </div>
        <div className="price">
          {props.onSale && (
            <del
              className="rs__result-card-product__price-sale"
              style={{
                textDecoration: "line-through",
              }}
            >
              <span className="amount">{formatMoney(prices?.compare_at_price?.amount * 100)} </span>
            </del>
          )}
          <span className="rs__result-card-product__price">{formatMoney(prices?.price?.amount * 100)}</span>
        </div>
      </div>
    </div>
  );
};

export const Wishlist: React.FC = ({ productId }) => {
  if (!window?.StatusAccounts) {
    return null;
  }

  return (
    <span className="product-card-wishlist">
      <status-favorite-button product-id={productId}></status-favorite-button>
    </span>
  );
};

export const ProductSizes: React.FC = ({ productHandle, sizes }) => {
  return (
    <div className="product-card-sizes-wrapper mt-6">
      {sizes.map((size, index) => (
        <a
          key={index}
          href={`${productHandle}?variant=${size.id}`}
          className={`product-card-size-item text-md ${!size?.available ? "disabled" : ""}`}
        >
          {size?.option1}
        </a>
      ))}
    </div>
  );
};

